import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, CalendarDays, Users, Scissors, MessageSquare, Bot, LogOut, DollarSign, Map } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";

const links = [
  { to: "/groomer", end: true, icon: LayoutDashboard, label: "Dashboard", tid: "nav-dashboard" },
  { to: "/groomer/calendar", icon: CalendarDays, label: "Calendar", tid: "nav-calendar" },
  { to: "/groomer/map", icon: Map, label: "Map", tid: "nav-map" },
  { to: "/groomer/clients", icon: Users, label: "Clients", tid: "nav-clients" },
  { to: "/groomer/services", icon: Scissors, label: "Services", tid: "nav-services" },
  { to: "/groomer/messages", icon: MessageSquare, label: "Messages", tid: "nav-messages" },
  { to: "/groomer/revenue", icon: DollarSign, label: "Revenue", tid: "nav-revenue" },
  { to: "/groomer/ai", icon: Bot, label: "AI Assist", tid: "nav-ai" },
];

export default function GroomerLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#1F1A17] flex">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-64 flex-col border-r border-[#E5DCCE] bg-white p-5 gap-1">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 rounded-xl bg-[#C85A17] grid place-content-center text-white">
            <Scissors className="w-4 h-4"/>
          </div>
          <div>
            <div className="font-display font-bold text-lg leading-none">PawTrek</div>
            <div className="text-xs text-[#6B625B] mt-1">Groomer Pro</div>
          </div>
        </div>
        {links.map(({ to, end, icon: Icon, label, tid }) => (
          <NavLink key={to} to={to} end={end} data-testid={tid}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                isActive
                  ? "bg-[#F5E6CF] text-[#A9480F]"
                  : "text-[#6B625B] hover:bg-[#F3EDE4]"
              }`
            }>
            <Icon className="w-4 h-4"/> {label}
          </NavLink>
        ))}
        <div className="mt-auto pt-4 border-t border-[#E5DCCE]">
          <div className="text-xs text-[#6B625B] mb-2 px-1">
            <div className="font-semibold text-[#1F1A17] truncate">{user?.name}</div>
            <div className="truncate">{user?.email}</div>
          </div>
          <Button variant="ghost" onClick={() => { logout(); nav("/"); }}
            className="w-full justify-start rounded-xl text-[#6B625B]" data-testid="btn-logout">
            <LogOut className="w-4 h-4 mr-2"/> Sign out
          </Button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-30 bg-white border-b border-[#E5DCCE] px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[#C85A17] grid place-content-center text-white">
            <Scissors className="w-4 h-4"/>
          </div>
          <span className="font-display font-bold">PawTrek</span>
        </div>
        <Button size="sm" variant="ghost" onClick={() => { logout(); nav("/"); }} data-testid="btn-logout-mobile">
          <LogOut className="w-4 h-4"/>
        </Button>
      </div>

      {/* Content */}
      <main className="flex-1 md:pt-0 pt-16 pb-24 md:pb-6 overflow-y-auto">
        <Outlet />
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-[#E5DCCE] grid grid-cols-8">
        {links.map(({ to, end, icon: Icon, label, tid }) => (
          <NavLink key={to} to={to} end={end} data-testid={`${tid}-mobile`}
            className={({ isActive }) =>
              `flex flex-col items-center gap-0.5 py-2 text-[10px] font-semibold ${
                isActive ? "text-[#C85A17]" : "text-[#6B625B]"
              }`
            }>
            <Icon className="w-5 h-5"/> {label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
